import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faClock, faTrash } from '@fortawesome/free-solid-svg-icons';
import Modal from './Modal';
import { API_BASE_URL } from '../../api';

import './CreateProductModal.scss';

function formatPeriodDate(value) {
    return new Date(value).toLocaleString('fr-FR', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
}

function periodStatus(period) {
    const now = new Date();
    const start = new Date(period.startDate);
    const end = new Date(period.endDate);
    if (now < start) return { label: 'À venir', bg: '#eff6ff', color: '#1d4ed8' };
    if (now > end) return { label: 'Terminée', bg: '#f3f4f6', color: '#6b7280' };
    return { label: 'En cours', bg: '#fff7ed', color: '#f59e0b' };
}

export default function PharmacyGardePeriodsModal({ show, onClose, pharmacy }) {
    const [periods, setPeriods] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!show || !pharmacy?.id) return;
        setLoading(true);
        axios
            .get(`${API_BASE_URL}/api/v1/pharmacies/${pharmacy.id}/garde`, { withCredentials: true })
            .then(({ data }) => setPeriods(data))
            .catch(() => toast.error('Impossible de charger les périodes de garde.'))
            .finally(() => setLoading(false));
    }, [show, pharmacy?.id]);

    // Only current/upcoming periods are shown here — past ones aren't relevant to manage
    // going forward, and dedicated garde history isn't this modal's purpose.
    const visiblePeriods = periods.filter((p) => new Date(p.endDate) >= new Date());

    const handleDelete = (id) => {
        axios
            .delete(`${API_BASE_URL}/api/v1/pharmacies/${pharmacy.id}/garde/${id}`, { withCredentials: true })
            .then(() => {
                setPeriods((prev) => prev.filter((p) => p.id !== id));
                toast.success('Période de garde supprimée.');
            })
            .catch(() => toast.error('Une erreur est survenue.'));
    };

    return (
        <Modal
            title={pharmacy ? `Périodes de garde — ${pharmacy.name}` : 'Périodes de garde'}
            show={show}
            onClose={onClose}
            icon={faCalendarDays}
            iconBg="#fff7ed"
            iconColor="#f59e0b"
            footer={
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <span style={{ fontSize: 13, color: '#6b7280' }}>
                        <strong style={{ color: '#111827' }}>{visiblePeriods.length}</strong> période{visiblePeriods.length !== 1 ? 's' : ''}
                    </span>
                    <button type="button" className="btn btn-light btn-sm" onClick={onClose}>
                        Fermer
                    </button>
                </div>
            }
        >
            {loading && (
                <div className="text-muted small text-center py-3">Chargement…</div>
            )}

            {!loading && visiblePeriods.length === 0 && (
                <div className="d-flex flex-column align-items-center gap-2 text-muted py-4">
                    <FontAwesomeIcon icon={faCalendarDays} style={{ fontSize: 22, opacity: 0.35 }} />
                    <span className="small">Aucune période de garde définie.</span>
                </div>
            )}

            <div className="d-flex flex-column gap-2">
                {visiblePeriods.map((period) => {
                    const status = periodStatus(period);
                    return (
                        <div
                            key={period.id}
                            className="d-flex align-items-center justify-content-between"
                            style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: 10, padding: '10px 14px' }}
                        >
                            <div className="d-flex align-items-center gap-3">
                                <span style={{
                                    width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                                    background: status.bg, color: status.color,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <FontAwesomeIcon icon={faClock} style={{ fontSize: 13 }} />
                                </span>
                                <div>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                                        {formatPeriodDate(period.startDate)} → {formatPeriodDate(period.endDate)}
                                    </div>
                                    <span style={{
                                        display: 'inline-block', marginTop: 4,
                                        background: status.bg, color: status.color,
                                        fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '2px 8px',
                                    }}>
                                        {status.label}
                                    </span>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="cdm-remove-btn"
                                onClick={() => handleDelete(period.id)}
                                title="Supprimer"
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </button>
                        </div>
                    );
                })}
            </div>
        </Modal>
    );
}
